import React, { createContext, useContext, useMemo } from "react";

/**
 * Whether the Settings section currently on screen is editable by this user.
 *
 * Set once per section by SettingsPage from the role's settings_<section>.edit
 * grant, and consumed by EditableCardFooter — so a view-only grant hides the
 * Edit button on every card in the section without each card needing to know
 * about permissions. Purely presentational: the matching PUT/POST is gated
 * server-side by require_settings_page, which is the boundary that counts.
 *
 * Defaults to editable so any card rendered outside a provider (tests, a
 * one-off screen) behaves exactly as it did before this existed.
 */
interface SettingsAccessValue {
  canEdit: boolean;
}

const SettingsAccessContext = createContext<SettingsAccessValue>({ canEdit: true });

export const useSettingsAccess = (): SettingsAccessValue => useContext(SettingsAccessContext);

export const SettingsAccessProvider: React.FC<{
  canEdit: boolean;
  children: React.ReactNode;
}> = ({ canEdit, children }) => {
  const value = useMemo(() => ({ canEdit }), [canEdit]);
  return (
    <SettingsAccessContext.Provider value={value}>
      {children}
    </SettingsAccessContext.Provider>
  );
};
